import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
   CButton, CCard, CCardBody, CCardHeader, CForm, CFormInput, CFormSelect,
   CCardFooter
} from '@coreui/react';

import { apiClient } from '../../services/Api';
import { jwtDecode } from 'jwt-decode';
import ToastNotification from "components/globals/ToastNotification";
import { triggerAlert } from 'components/globals/AlertNotification';

const User = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const [formData, setFormData] = useState(null);
   const location = useLocation();
   const [toast, setToast] = useState(null);

   const token = localStorage.getItem("access_token");
   const decodedToken = jwtDecode(token);
   const is_director = decodedToken.is_director;
   const isStaff = decodedToken.is_staff;
   const user_id = decodedToken.user_id;

   
   useEffect(() => {
      const fetchUserDetails = async () => {
         let data = null; // Declare a variable to store API response
         setToast(null); // Clear any existing toast messages
   
         try {
            const response = await apiClient.get(`/users/by-ids/`, {
               params: { "ids[]": [id] } // Pass a single user ID as an array
            });
            data = response.data; // Store response data
            setFormData(data.length > 0 ? data[0] : {}); // Ensure we only use the first item
         } catch (error) {
            triggerAlert('Utilizador não foi encontrado!', 'error');
            navigate("/utilizadores");
         }
      };

      if (!id) {
         setFormData({});
         setToast(null); // Clear any existing toast messages
      } else {
         fetchUserDetails();
      }
   }, [id, location.pathname, navigate]);

   const validatePassword = (password) => {
      const lengthRequirement = password.length >= 8;
      const hasUppercase = /[A-Z]/.test(password);
      const hasLowercase = /[a-z]/.test(password);
      const hasNumber = /\d/.test(password);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

      const validCategories = [hasUppercase, hasLowercase, hasNumber, hasSpecialChar].filter(Boolean).length;

      return lengthRequirement && validCategories >= 3;
   };

   const handleInputChange = (e) => {
      const { name, value, type, checked } = e.target;
      let updatedValue = value || null;

      if (type === "checkbox") {
         updatedValue = checked;
      } else if (value === "true") {
         updatedValue = true;
      } else if (value === "false") {
         updatedValue = false;
      }

      setFormData((prevFormData) => ({
         ...prevFormData,
         [name]: updatedValue,
      }));
   };

   const handleSubmit = async (e) => {
      e.preventDefault();

      if ((!id || user_id === id) && formData.password) {
         if (!validatePassword(formData.password)) {
            setToast({
               title: 'Password',
               message: "A senha deve ter pelo menos 8 caracteres e incluir pelo menos três dos seguintes: letra maiúscula, letra minúscula, número ou caractere especial.",
               type: "error",
            });
            return;
         }
         if (formData.password !== formData.confirm_password) {
            setToast({
               title: 'Password',
               message: "As senhas não coincidem.",
               type: "error",
            });
            return;
         }
      }

      try {
         let requestData = { ...formData };

         // If updating an existing user, exclude the password field if not changing it
         if (id && !formData.password) {
            delete requestData.password;
            delete requestData.confirm_password;
         }

         if (id) {
            await apiClient.put(`/users/${id}/`, requestData);
            triggerAlert('Utilizador atualizado com sucesso!', 'sucesso');
         } else {
            await apiClient.post("/users/", requestData);
            triggerAlert('Utilizador criado com sucesso!', 'sucesso');
         }
         navigate("/utilizadores");
      } catch (error) {
         const errorMessage = error.response?.data?.error || "Erro ao gravar utilizador";
         setToast({ title: 'Utilizador', message: errorMessage, type: "error" });
      }
   };

   if (formData !== null) {
      if (user_id === formData.id || is_director || isStaff) {
         return (
            <div className="container mt-4">
               <ToastNotification toast={toast} setToast={setToast} />

               <CCard>
                  <CCardHeader>
                     <h3>{id ? 'Editar Utilizador' : 'Novo Utilizador'}</h3>
                  </CCardHeader>
                  <CCardBody>
                     <CForm onSubmit={handleSubmit} autoComplete="off">
                        <CFormInput
                           label={<strong>Nome</strong>}
                           id="first_name"
                           name="first_name"
                           value={formData.first_name || ""}
                           onChange={handleInputChange}
                           required
                           className="mb-3"
                           disabled={id && user_id === formData.id} // Disable if editing self
                        />
                        <CFormInput
                           label={<strong>Apelido</strong>}
                           id="last_name"
                           name="last_name"
                           value={formData.last_name || ""}
                           onChange={handleInputChange}
                           required
                           className="mb-3"
                           disabled={id && user_id === formData.id} // Disable if editing self
                        />
                        <CFormInput
                           label={<strong>Email</strong>}
                           id="email"
                           name="email"
                           value={formData.email || ""}
                           onChange={handleInputChange}
                           required
                           className="mb-3"
                           autoComplete="off"
                           disabled={id && user_id === formData.id} // Disable if editing self
                        />

                        <CFormSelect
                           label={<strong>Cargo</strong>}
                           id="role"
                           name="role"
                           value={formData.role?.toString() || ""} // Convert boolean to string for select
                           onChange={handleInputChange}
                           required
                           className="mb-3"
                           disabled={id && user_id === formData.id} // Disable if editing self
                        >
                           <option value="">Selecione o cargo</option>
                           <option value="Estagiário">Estagiário</option>
                           <option value="Analista">Analista</option>
                           <option value="Consultor">Consultor</option>
                           <option value="Consultor Sénior">Consultor Sénior</option>
                           <option value="Gestor de Área">Gestor de Área</option>
                           <option value="Diretor">Diretor</option>
                           <option value="Gerente">Gerente</option>
                        </CFormSelect>

                        <CFormSelect
                           label={<strong>Estado</strong>}
                           id="is_active"
                           name="is_active"
                           value={formData.is_active?.toString() || ""} // Convert boolean to string for select
                           onChange={handleInputChange}
                           required
                           className="mb-3"
                           disabled={id && user_id === formData.id} // Disable if editing self
                        >
                           <option value="" disabled>Selecione o estado</option>
                           <option value="true">Ativo</option>
                           <option value="false">Inativo</option>
                        </CFormSelect>

                        {(!id || user_id === formData.id) && (
                           <>
                              <CFormInput
                                 label={<strong>Password</strong>}
                                 id="password"
                                 name="password"
                                 type="password"
                                 onChange={handleInputChange}
                                 required={!id} // Only required if creating a new user
                                 className="mb-3"
                                 autoComplete="new-password"
                              />
                              <CFormInput
                                 label={<strong>Confirmar Password</strong>}
                                 id="confirm_password"
                                 name="confirm_password"
                                 type="password"
                                 onChange={handleInputChange}
                                 required={!id}
                                 className="mb-3"
                                 autoComplete="new-password"
                              />
                           </>
                        )}
                     </CForm>
                  </CCardBody>
                  <CCardFooter className="d-flex justify-content-end gap-2">
                     <CButton color="danger" className="text-white" onClick={() => navigate("/utilizadores")}>
                        Cancelar
                     </CButton>
                     <CButton color="success" className="text-white" type="submit" onClick={handleSubmit}>
                        Confirmar
                     </CButton>
                  </CCardFooter>
               </CCard>
            </div>
         );
      }
   };
}

export default User;
